<template>
    <div class="cl-page">
        <!-- Header -->
        <div class="cl-header">
            <div>
                <h2 class="cl-title">Clients</h2>
                <p class="cl-subtitle">Manage project clients and their contact persons</p>
            </div>
            <button class="cl-add-btn" @click="openModal()">
                <i class="fas fa-plus"></i> Add Client
            </button>
        </div>

        <!-- Search -->
        <div class="cl-toolbar">
            <div class="cl-search-wrap">
                <i class="fas fa-search cl-search-icon"></i>
                <input v-model="search" class="cl-search-input" placeholder="Search by name, company, email…" />
            </div>
        </div>

        <!-- Loading -->
        <div v-if="loadingData" class="cl-loading">
            <i class="fas fa-circle-notch fa-spin"></i> Loading clients…
        </div>

        <!-- Table -->
        <div v-else class="cl-table-wrap">
            <table class="cl-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Name</th>
                        <th>Company</th>
                        <th>Email</th>
                        <th>Mobile</th>
                        <th>Location</th>
                        <th>Contact Person</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(client, i) in filteredClients" :key="client.id">
                        <td class="cl-num">{{ i + 1 }}</td>
                        <td>
                            <div class="cl-name">{{ client.name }}</div>
                        </td>
                        <td>{{ client.companyName || '—' }}</td>
                        <td>
                            <a v-if="client.email" :href="'mailto:' + client.email" class="cl-link">{{ client.email }}</a>
                            <span v-else>—</span>
                        </td>
                        <td>{{ client.mobile || '—' }}</td>
                        <td>
                            <span v-if="client.city || client.state || client.country">
                                {{ [client.city, client.state, client.country].filter(Boolean).join(', ') }}
                            </span>
                            <span v-else>—</span>
                        </td>
                        <td>
                            <div v-if="client.contactPersonName" class="cl-contact">
                                <div class="cl-contact-name">{{ client.contactPersonName }}</div>
                                <div class="cl-contact-meta" v-if="client.contactPersonPhone">
                                    <i class="fas fa-phone"></i> {{ client.contactPersonPhone }}
                                </div>
                            </div>
                            <span v-else>—</span>
                        </td>
                        <td>
                            <div class="cl-actions">
                                <button class="cl-btn cl-btn-edit" @click="openModal(client)">
                                    <i class="fas fa-pencil-alt"></i>
                                </button>
                                <button class="cl-btn cl-btn-del" @click="confirmDelete(client)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </td>
                    </tr>
                    <tr v-if="filteredClients.length === 0">
                        <td colspan="8" class="cl-empty">
                            <i class="fas fa-users"></i>
                            <div>No clients found</div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Add / Edit Modal -->
        <transition name="cl-fade">
            <div v-if="modal.show" class="cl-modal-overlay" @click.self="closeModal">
                <div class="cl-modal">
                    <div class="cl-modal-header">
                        <h3 class="cl-modal-title">{{ modal.editing ? 'Edit Client' : 'Add Client' }}</h3>
                        <button class="cl-modal-close" @click="closeModal"><i class="fas fa-times"></i></button>
                    </div>

                    <div class="cl-modal-body">
                        <!-- Section: Client Info -->
                        <div class="cl-form-section">
                            <div class="cl-section-label">
                                <i class="fas fa-user"></i> Client Information
                            </div>
                            <div class="cl-form-grid">
                                <div class="cl-form-group">
                                    <label class="cl-label">Name <span class="cl-req">*</span></label>
                                    <input v-model="form.name" class="cl-input" placeholder="Client full name" />
                                    <span v-if="errors.name" class="cl-err">{{ errors.name }}</span>
                                </div>
                                <div class="cl-form-group">
                                    <label class="cl-label">Company Name</label>
                                    <input v-model="form.companyName" class="cl-input" placeholder="Company or organization" />
                                </div>
                                <div class="cl-form-group">
                                    <label class="cl-label">Email</label>
                                    <input v-model="form.email" class="cl-input" type="email" placeholder="client@example.com" />
                                    <span v-if="errors.email" class="cl-err">{{ errors.email }}</span>
                                </div>
                                <div class="cl-form-group">
                                    <label class="cl-label">Mobile</label>
                                    <input v-model="form.mobile" class="cl-input" placeholder="+91 9876543210" />
                                </div>
                            </div>
                        </div>

                        <!-- Section: Location -->
                        <div class="cl-form-section">
                            <div class="cl-section-label">
                                <i class="fas fa-map-marker-alt"></i> Location
                            </div>
                            <CountryStateCitySelector
                                :countryId="form.country"
                                :stateId="form.state"
                                :cityId="form.city"
                                @country="form.country = $event"
                                @state="form.state = $event"
                                @city="form.city = $event"
                            />
                        </div>

                        <!-- Section: Contact Person -->
                        <div class="cl-form-section">
                            <div class="cl-section-label">
                                <i class="fas fa-id-card"></i> Contact Person
                            </div>
                            <div class="cl-form-grid">
                                <div class="cl-form-group">
                                    <label class="cl-label">Name</label>
                                    <input v-model="form.contactPersonName" class="cl-input" placeholder="Contact person name" />
                                </div>
                                <div class="cl-form-group">
                                    <label class="cl-label">Email</label>
                                    <input v-model="form.contactPersonEmail" class="cl-input" type="email" placeholder="contact@example.com" />
                                </div>
                                <div class="cl-form-group">
                                    <label class="cl-label">Phone</label>
                                    <input v-model="form.contactPersonPhone" class="cl-input" placeholder="+91 9876543210" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="cl-modal-footer">
                        <button class="cl-cancel-btn" @click="closeModal" :disabled="saving">Cancel</button>
                        <button class="cl-save-btn" @click="saveClient" :disabled="saving">
                            <i v-if="saving" class="fas fa-circle-notch fa-spin"></i>
                            <i v-else class="fas fa-check"></i>
                            {{ saving ? 'Saving…' : (modal.editing ? 'Update Client' : 'Add Client') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Delete Confirm Modal -->
        <transition name="cl-fade">
            <div v-if="deleteModal.show" class="cl-modal-overlay" @click.self="deleteModal.show = false">
                <div class="cl-modal cl-modal-sm">
                    <div class="cl-modal-header">
                        <h3 class="cl-modal-title">Delete Client</h3>
                        <button class="cl-modal-close" @click="deleteModal.show = false"><i class="fas fa-times"></i></button>
                    </div>
                    <div class="cl-modal-body">
                        <p class="cl-del-text">Are you sure you want to delete <strong>{{ deleteModal.client && deleteModal.client.name }}</strong>? This action cannot be undone.</p>
                    </div>
                    <div class="cl-modal-footer">
                        <button class="cl-cancel-btn" @click="deleteModal.show = false" :disabled="deleting">Cancel</button>
                        <button class="cl-del-btn" @click="deleteClient" :disabled="deleting">
                            <i v-if="deleting" class="fas fa-circle-notch fa-spin"></i>
                            <i v-else class="fas fa-trash"></i>
                            {{ deleting ? 'Deleting…' : 'Delete' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import { http } from "@/utils/http"
import CountryStateCitySelector from "@/components/UI/CountryStateCitySelector.vue"

const emptyForm = () => ({
    name: "",
    companyName: "",
    email: "",
    mobile: "",
    country: "",
    state: "",
    city: "",
    contactPersonName: "",
    contactPersonEmail: "",
    contactPersonPhone: "",
})

export default {
    components: { CountryStateCitySelector },
    data() {
        return {
            loadingData: false,
            saving: false,
            deleting: false,
            clients: [],
            search: "",
            form: emptyForm(),
            errors: {},
            modal: { show: false, editing: false, id: null },
            deleteModal: { show: false, client: null },
        }
    },
    async mounted() {
        await this.fetchClients()
    },
    computed: {
        filteredClients() {
            if (!this.search.trim()) return this.clients
            const q = this.search.trim().toLowerCase()
            return this.clients.filter(
                (c) =>
                    (c.name || "").toLowerCase().includes(q) ||
                    (c.companyName || "").toLowerCase().includes(q) ||
                    (c.email || "").toLowerCase().includes(q) ||
                    (c.contactPersonName || "").toLowerCase().includes(q)
            )
        },
    },
    methods: {
        async fetchClients() {
            try {
                this.loadingData = true
                const { data } = await http.get("/project-clients")
                this.clients = data.result || []
            } catch (err) {
                console.error("Error fetching clients:", err)
            } finally {
                this.loadingData = false
            }
        },
        openModal(client = null) {
            this.errors = {}
            if (client) {
                this.form = {
                    name: client.name || "",
                    companyName: client.companyName || "",
                    email: client.email || "",
                    mobile: client.mobile || "",
                    country: client.country || "",
                    state: client.state || "",
                    city: client.city || "",
                    contactPersonName: client.contactPersonName || "",
                    contactPersonEmail: client.contactPersonEmail || "",
                    contactPersonPhone: client.contactPersonPhone || "",
                }
                this.modal = { show: true, editing: true, id: client.id }
            } else {
                this.form = emptyForm()
                this.modal = { show: true, editing: false, id: null }
            }
        },
        closeModal() {
            this.modal.show = false
        },
        validate() {
            const errs = {}
            if (!this.form.name.trim()) errs.name = "Name is required"
            if (this.form.email && !/^\S+@\S+\.\S+$/.test(this.form.email)) errs.email = "Invalid email"
            this.errors = errs
            return Object.keys(errs).length === 0
        },
        async saveClient() {
            if (!this.validate()) return
            try {
                this.saving = true
                if (this.modal.editing) {
                    await http.put(`/project-clients/${this.modal.id}`, this.form)
                } else {
                    await http.post("/project-clients", this.form)
                }
                await this.fetchClients()
                this.closeModal()
                this.$toast && this.$toast.success(this.modal.editing ? "Client updated" : "Client added")
            } catch (err) {
                console.error("Error saving client:", err)
                this.$toast && this.$toast.error("Error saving client")
            } finally {
                this.saving = false
            }
        },
        confirmDelete(client) {
            this.deleteModal = { show: true, client }
        },
        async deleteClient() {
            try {
                this.deleting = true
                await http.delete(`/project-clients/${this.deleteModal.client.id}`)
                await this.fetchClients()
                this.deleteModal.show = false
                this.$toast && this.$toast.success("Client deleted")
            } catch (err) {
                console.error("Error deleting client:", err)
                this.$toast && this.$toast.error("Error deleting client")
            } finally {
                this.deleting = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import "@/assets/variables.scss";

.cl-page { padding: 20px; max-width: 1300px; }

.cl-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    flex-wrap: wrap; gap: 12px; margin-bottom: 20px;
}
.cl-title  { font-size: 20px; font-weight: 700; color: $gray800; margin: 0; }
.cl-subtitle { font-size: 13px; color: $gray500; margin: 3px 0 0; }

.cl-add-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 9px 16px; background: $primaryColor; color: white; border: none;
    border-radius: $radiusSm; font-size: 13px; font-weight: 500; cursor: pointer;
    transition: opacity 0.15s;
    &:hover { opacity: 0.88; }
    i { font-size: 12px; }
}

.cl-toolbar { margin-bottom: 16px; }
.cl-search-wrap { position: relative; display: inline-flex; align-items: center; }
.cl-search-icon { position: absolute; left: 10px; color: $gray400; font-size: 13px; pointer-events: none; }
.cl-search-input {
    padding: 8px 12px 8px 32px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; width: 280px; outline: none;
    &:focus { border-color: $primaryColor; }
}

.cl-loading { padding: 48px; text-align: center; color: $gray400; font-size: 15px; i { margin-right: 8px; } }

.cl-table-wrap {
    background: white; border: 1px solid $gray200; border-radius: $radiusMd;
    overflow: hidden; box-shadow: $shadowSm; overflow-x: auto;
}
.cl-table {
    width: 100%; border-collapse: collapse; font-size: 13px;
    thead tr { background: $gray50; border-bottom: 1px solid $gray200; }
    th {
        padding: 10px 14px; text-align: left; font-weight: 600; font-size: 12px;
        color: $gray500; white-space: nowrap; text-transform: uppercase; letter-spacing: 0.03em;
    }
    td { padding: 12px 14px; border-bottom: 1px solid $gray100; vertical-align: middle; color: $gray700; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: $gray50; }
}
.cl-num { color: $gray400; font-size: 12px; }
.cl-name { font-weight: 600; color: $gray800; }
.cl-link { color: $primaryColor; text-decoration: none; &:hover { text-decoration: underline; } }
.cl-contact-name { font-weight: 500; color: $gray800; font-size: 13px; }
.cl-contact-meta { font-size: 11px; color: $gray500; margin-top: 2px; i { margin-right: 3px; } }
.cl-actions { display: flex; gap: 6px; }
.cl-btn {
    width: 30px; height: 30px; border: 1px solid $gray200; border-radius: $radiusSm;
    background: white; cursor: pointer; display: flex; align-items: center; justify-content: center;
    font-size: 12px; transition: all 0.15s;
}
.cl-btn-edit { color: $primaryColor; &:hover { background: $primaryLight; border-color: $primaryColor; } }
.cl-btn-del  { color: #ef4444; &:hover { background: #fee2e2; border-color: #ef4444; } }
.cl-empty { text-align: center; padding: 48px; color: $gray400; i { display: block; font-size: 28px; margin-bottom: 8px; } }

/* Modal */
.cl-modal-overlay {
    position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000;
    display: flex; align-items: center; justify-content: center; padding: 20px;
}
.cl-modal {
    background: white; border-radius: $radiusMd; width: 100%; max-width: 680px;
    max-height: 90vh; display: flex; flex-direction: column; box-shadow: $shadowXl;
}
.cl-modal-sm { max-width: 420px; }
.cl-modal-header {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 20px; border-bottom: 1px solid $gray100;
}
.cl-modal-title { font-size: 16px; font-weight: 700; color: $gray800; margin: 0; }
.cl-modal-close {
    width: 30px; height: 30px; border: none; background: $gray100; border-radius: $radiusSm;
    color: $gray500; cursor: pointer; display: flex; align-items: center; justify-content: center;
    transition: all 0.15s;
    &:hover { background: $gray200; color: $gray800; }
}
.cl-modal-body { padding: 20px; overflow-y: auto; flex: 1; }
.cl-modal-footer {
    display: flex; justify-content: flex-end; gap: 10px;
    padding: 14px 20px; border-top: 1px solid $gray100;
}

/* Form */
.cl-form-section { margin-bottom: 24px; }
.cl-section-label {
    font-size: 12px; font-weight: 700; color: $gray500; text-transform: uppercase;
    letter-spacing: 0.05em; margin-bottom: 12px; display: flex; align-items: center; gap: 7px;
    padding-bottom: 8px; border-bottom: 1px solid $gray100;
    i { color: $primaryColor; font-size: 13px; }
}
.cl-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 520px) { .cl-form-grid { grid-template-columns: 1fr; } }
.cl-form-group { display: flex; flex-direction: column; gap: 5px; }
.cl-label { font-size: 12px; font-weight: 600; color: $gray600; }
.cl-req { color: #ef4444; }
.cl-input {
    padding: 8px 11px; border: 1px solid $gray200; border-radius: $radiusSm;
    font-size: 13px; color: $gray800; outline: none; transition: border-color 0.15s;
    &:focus { border-color: $primaryColor; }
    &::placeholder { color: $gray400; }
}
.cl-err { font-size: 11px; color: #ef4444; margin-top: 1px; }

/* Footer buttons */
.cl-cancel-btn {
    padding: 8px 16px; border: 1px solid $gray200; border-radius: $radiusSm;
    background: white; color: $gray600; font-size: 13px; cursor: pointer;
    transition: all 0.15s;
    &:hover:not(:disabled) { background: $gray50; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.cl-save-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 18px; background: $primaryColor; color: white; border: none;
    border-radius: $radiusSm; font-size: 13px; font-weight: 500; cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.cl-del-btn {
    display: flex; align-items: center; gap: 7px;
    padding: 8px 18px; background: #ef4444; color: white; border: none;
    border-radius: $radiusSm; font-size: 13px; font-weight: 500; cursor: pointer;
    transition: opacity 0.15s;
    &:hover:not(:disabled) { opacity: 0.88; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.cl-del-text { font-size: 14px; color: $gray700; line-height: 1.6; }

/* Transition */
.cl-fade-enter-active, .cl-fade-leave-active { transition: opacity 0.2s; }
.cl-fade-enter, .cl-fade-leave-to { opacity: 0; }
</style>
